import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarClock, Clock3, Loader2 } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingModal from "@/components/BookingModal";
import { useLenis } from "@/hooks/usePageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useLenis();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/blogs/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/blogs`)
      .then((r) => setRelated(r.data.filter((b) => b.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="blog-not-found">
        <p className="font-display text-2xl font-bold">Post not found</p>
        <Link to="/blog" className="bg-amber-400 text-slate-900 font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-amber-500 transition-colors">
          Back to Blog
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-loading">
        <Loader2 size={32} className="text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen" data-testid="blog-post-page">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 pt-32 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-amber-700 transition-colors mb-12" data-testid="blog-back-link">
          <ArrowLeft size={15} /> Back to all posts
        </Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <span className="inline-block font-mono2 text-[10px] uppercase tracking-[0.2em] text-sky-600 border border-sky-600/30 bg-sky-600/10 px-3 py-1 rounded-full mb-5">
            {post.category}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-5" data-testid="blog-post-title">
            {post.title}
          </h1>
          <p className="font-mono2 text-[11px] text-slate-500 flex items-center gap-2 mb-14">
            <Clock3 size={12} /> {post.read_time} read • {fmtDate(post.date)} • Vikram Fotani
          </p>
        </motion.div>

        {post.sections.map((s, i) => (
          <motion.section
            key={s.heading}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-12"
            data-testid={`blog-section-${i}`}
          >
            <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight mb-4 flex items-baseline gap-3">
              <span className="font-mono2 text-sm text-amber-600/60">{String(i + 1).padStart(2, "0")}</span>
              {s.heading}
            </h2>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
          </motion.section>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-16 rounded-3xl border border-amber-600/30 bg-amber-400/10 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          data-testid="blog-cta-card"
        >
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight mb-2">Working on this in your landscape?</h3>
            <p className="text-sm text-slate-600">Book a free 15-min call or explore the related course programs.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
              className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-6 py-3 rounded-full hover:bg-amber-500 transition-colors"
              data-testid="blog-cta-book-call"
            >
              <CalendarClock size={15} /> Book a call
            </button>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 text-sm font-medium px-6 py-3 rounded-full hover:border-amber-600/50 hover:text-amber-700 transition-colors"
              data-testid="blog-cta-courses"
            >
              View courses <ArrowUpRight size={14} />
            </Link>
          </div>
        </motion.div>
      </article>

      {related.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 pb-24">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-8">Keep reading</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                className="group rounded-3xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:border-amber-600/40 transition-all duration-300"
                data-testid={`related-post-${r.slug}`}
              >
                <p className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-sky-600 mb-3">{r.category}</p>
                <h3 className="font-display text-sm font-bold leading-snug group-hover:text-amber-700 transition-colors">{r.title}</h3>
              </Link>
            ))}
          </div>
        </section>
      )}
      <Footer />
      <BookingModal />
    </div>
  );
};

export default BlogPost;
